import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/data/content";

const services = ["General Inquiry", ...SERVICES.map((s) => s.title)];
const budgets = ["< $1k / mo", "$1k – $3k / mo", "$3k – $7k / mo", "$7k+ / mo", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "General Inquiry", budget: "Not sure yet", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thanks! We'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand";

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pt-40 pb-12">
        <div className="pointer-events-none absolute -right-32 top-24 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Contact</span></Reveal>
          <Reveal delay={0.1}><h1 className="mt-5 max-w-4xl font-display text-5xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl">Let's build your growth plan.</h1></Reveal>
        </div>
      </section>

      <section className="relative z-10 pb-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1fr_1.4fr] lg:px-10">
          <Reveal>
            <div className="space-y-8">
              <p className="text-lg leading-relaxed text-slate-600">Tell us a bit about your business and goals. We'll reply within one business day with a free, tailored growth plan.</p>
              <div className="space-y-4">
                <a href="mailto:joellegend247@gmail.com" className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-brand">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand"><Mail className="h-5 w-5" /></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Email</p><p className="font-semibold text-slate-900">joellegend247@gmail.com</p></div>
                </a>
                <a href="tel:9707463660" className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-brand">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand"><Phone className="h-5 w-5" /></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Phone</p><p className="font-semibold text-slate-900">(970) 746-3660</p></div>
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="contact-success" className="flex h-full flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white p-12 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand text-white"><Check className="h-8 w-8" /></div>
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">Message received!</h3>
                <p className="mt-2 text-slate-600">Thanks, {form.name.split(" ")[0]}. Our team will reach out within 1 business day.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Full name *</label><input data-testid="contact-name" className={inputCls} value={form.name} onChange={upd("name")} placeholder="Jane Doe" /></div>
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Email *</label><input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={upd("email")} placeholder="jane@business.com" /></div>
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Phone</label><input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={upd("phone")} placeholder="(555) 000-0000" /></div>
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Company</label><input data-testid="contact-company" className={inputCls} value={form.company} onChange={upd("company")} placeholder="Business name" /></div>
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Service</label><select data-testid="contact-service" className={inputCls} value={form.service} onChange={upd("service")}>{services.map((s) => <option key={s}>{s}</option>)}</select></div>
                  <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Monthly budget</label><select data-testid="contact-budget" className={inputCls} value={form.budget} onChange={upd("budget")}>{budgets.map((b) => <option key={b}>{b}</option>)}</select></div>
                </div>
                <div className="mt-5"><label className="mb-1.5 block text-sm font-medium text-slate-700">How can we help? *</label><textarea data-testid="contact-message" rows={4} className={inputCls} value={form.message} onChange={upd("message")} placeholder="Tell us about your business and goals…" /></div>
                <button type="submit" data-testid="contact-submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60">
                  {loading ? "Sending…" : <>Send my request <ArrowUpRight className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
